import { useState, useEffect, useCallback, useRef, memo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

// Import hero slide images - standard
import heroSlideStudio from "@/assets/hero-slide-studio.png";
import heroSlideUrban from "@/assets/hero-slide-urban.png";
import heroSlideUrbanHeadlights from "@/assets/hero-slide-urban-headlights.png";
import heroSlideArchitectural from "@/assets/hero-slide-architectural.png";

// Import hero video
import audiQ7HeroVideo from "@/assets/audi-q7-hero.mp4.asset.json";
import carouselSlide3Video from "@/assets/carousel-slide-3.mp4.asset.json";

// Import responsive srcsets for optimized loading
import heroSlideStudioSrcset from "@/assets/hero-slide-studio.png?responsive";
import heroSlideUrbanSrcset from "@/assets/hero-slide-urban.png?responsive";
import heroSlideArchitecturalSrcset from "@/assets/hero-slide-architectural.png?responsive";

interface ResponsiveSrcset {
  srcset: string;
  src: string;
}

interface Slide {
  image?: string;
  srcset?: ResponsiveSrcset;
  video?: string;
  mobileObjectPosition?: string;
  desktopObjectPosition?: string;
  alt: string;
  headline: string;
  headlineAccent?: string;
  subtext: string;
  buttonText: string;
  buttonLink: string;
}

const slides: Slide[] = [
  {
    video: carouselSlide3Video.url,
    image: heroSlideArchitectural,
    srcset: heroSlideArchitecturalSrcset as unknown as ResponsiveSrcset,
    mobileObjectPosition: "68% center",
    desktopObjectPosition: "center center",
    alt: "Professional VIP corporate transportation arriving at luxury hotel destination in San Antonio Texas",
    headline: "Professional. Punctual.",
    headlineAccent: "Exclusive.",
    subtext: "Serving SAT Airport, Hill Country, and Corporate Events.",
    buttonText: "GET A QUOTE",
    buttonLink: "/request-quote",
  },
  {
    image: heroSlideUrbanHeadlights,
    srcset: { src: heroSlideUrbanHeadlights, srcset: `${heroSlideUrbanHeadlights} 1x` } as unknown as ResponsiveSrcset,
    mobileObjectPosition: "56% center",
    desktopObjectPosition: "center center",
    alt: "Executive black car chauffeur service driving at night in San Antonio for SAT airport transfer",
    headline: "Transparent Pricing.",
    headlineAccent: "No Hidden Fees.",
    subtext: "Flat-Rate Airport Transfers & Hourly Charters.",
    buttonText: "VIEW RATES",
    buttonLink: "/services",
  },
  {
    image: heroSlideStudio,
    srcset: heroSlideStudioSrcset as unknown as ResponsiveSrcset,
    mobileObjectPosition: "62% center",
    desktopObjectPosition: "center center",
    alt: "2025 Audi Q7 Black Optic luxury SUV front view - Rick's Black Car Service San Antonio fleet",
    headline: "Effortless Luxury.",
    headlineAccent: "Unforgettable Journeys.",
    subtext: "Experience San Antonio's premier chauffeur service in our 2025 Audi Q7 Black Optic.",
    buttonText: "BOOK YOUR RIDE",
    buttonLink: "/book",
  },
];

// ---------------- Memoized slide media ----------------
// Extracted so React only re-renders the active slide's media when
// `currentSlide` or `isMobile` changes — nothing else in the parent
// (autoplay toggling, arrow hover, etc.) can trigger a re-render.
interface SlideMediaProps {
  slide: Slide;
  index: number;
  isActive: boolean;
  isMobile: boolean;
  videoRef?: React.RefObject<HTMLVideoElement>;
}

const SlideMedia = memo(
  ({ slide, index, isActive, isMobile, videoRef }: SlideMediaProps) => {
    const objectPosition = isMobile
      ? slide.mobileObjectPosition
      : slide.desktopObjectPosition;

    if (slide.video && (index === 0 || index === 2)) {
      return (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={slide.video}
          poster={slide.image}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          style={{ objectPosition }}
         disableRemotePlayback/>
      );
    }

    return (
      <img
        src={slide.srcset?.src || slide.image}
        srcSet={slide.srcset?.srcset}
        sizes="100vw"
        alt={slide.alt}
        loading={index === 0 ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={isActive && index === 0 ? "high" : "auto"}
        className="absolute inset-0 w-full h-full object-cover object-center"
        style={{ objectPosition }}
      />
    );
  },
  (prev, next) =>
    prev.isActive === next.isActive &&
    prev.isMobile === next.isMobile &&
    prev.index === next.index,
);
SlideMedia.displayName = "SlideMedia";

const LuxuryHeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoRef3 = useRef<HTMLVideoElement>(null);
  const autoplayResumeRef = useRef<number | null>(null);
  const isMobile = useIsMobile();

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const pauseAutoplay = useCallback(() => {
    setIsAutoPlaying(false);
    if (autoplayResumeRef.current) {
      window.clearTimeout(autoplayResumeRef.current);
    }
    autoplayResumeRef.current = window.setTimeout(
      () => setIsAutoPlaying(true),
      10000,
    );
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      setCurrentSlide(index);
      pauseAutoplay();
    },
    [pauseAutoplay],
  );

  const handlePrev = useCallback(() => {
    prevSlide();
    pauseAutoplay();
  }, [prevSlide, pauseAutoplay]);

  const handleNext = useCallback(() => {
    nextSlide();
    pauseAutoplay();
  }, [nextSlide, pauseAutoplay]);

  useEffect(() => {
    return () => {
      if (autoplayResumeRef.current) {
        window.clearTimeout(autoplayResumeRef.current);
      }
    };
  }, []);

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Ensure video plays when slide 0 is active
  useEffect(() => {
    if (currentSlide === 0 && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
    if (currentSlide === 2 && videoRef3.current) {
      videoRef3.current.currentTime = 0;
      videoRef3.current.play().catch(() => {});
    }
  }, [currentSlide]);

  // Warm the browser cache for the next slide image while the current
  // slide is on-screen so the crossfade doesn't wait on a network fetch.
  useEffect(() => {
    const nextIdx = (currentSlide + 1) % slides.length;
    const nextSrc =
      slides[nextIdx].srcset?.src || slides[nextIdx].image;
    if (!nextSrc) return;
    const img = new Image();
    if (slides[nextIdx].srcset?.srcset) {
      img.srcset = slides[nextIdx].srcset!.srcset;
      img.sizes = "100vw";
    }
    img.src = nextSrc;
  }, [currentSlide]);

  const activeSlide = slides[currentSlide];
  const activeVideoRef =
    currentSlide === 0 ? videoRef : currentSlide === 2 ? videoRef3 : undefined;

  return (
    <section className="relative h-screen min-h-[100dvh] w-full overflow-hidden bg-[#0D0D0D]">
      {/* Persistent Top-Left Branding - Always Visible */}
      <div className="absolute top-24 md:top-28 left-4 sm:left-6 md:left-12 z-30 max-w-[19rem] sm:max-w-md md:max-w-lg">
        <h1 
          className="font-playfair text-white text-[1.05rem] sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-[1.08]"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)' }}
        >
          <span className="text-[#C5A059]" style={{ textShadow: '0 0 15px rgba(197,160,89,0.8), 0 0 30px rgba(197,160,89,0.6), 0 0 50px rgba(212,175,55,0.4)' }}>Black Car Service</span> & Luxury Airport Transportation
          <span 
            className="block text-[#C5A059]/90 font-sans text-sm sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide mt-2"
            style={{ textShadow: '0 0 12px rgba(197,160,89,0.6), 0 0 25px rgba(197,160,89,0.4)' }}
          >
            in San Antonio
          </span>
        </h1>
      </div>

      {/* JBSA Base Access Badge - Bottom Left with Pulsing Gold Glow */}
      <Link
        to="/jbsa-military-transportation"
        className="absolute bottom-28 md:bottom-24 lg:bottom-12 left-4 md:left-12 z-30 group max-w-[calc(100vw-7rem)] md:max-w-none"
      >
        <div className="relative">
          {/* Animated Gold Glow Effect - Subtle */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-[#C5A059] via-[#D4AF37] to-[#C5A059] rounded-sm opacity-40 blur-sm animate-[pulse_3s_ease-in-out_infinite]" />
          <div className="absolute -inset-1 bg-gradient-to-r from-[#D4AF37] via-[#E2BE7A] to-[#D4AF37] rounded-sm opacity-20 blur-md animate-[pulse_3s_ease-in-out_infinite_0.5s]" />
          
          {/* Badge Content */}
          <div className="relative flex items-center gap-2 md:gap-3 bg-black/85 backdrop-blur-sm border border-[#C5A059]/60 rounded-sm px-3 md:px-4 py-2 md:py-3 transition-all duration-300 hover:bg-black/90 hover:border-[#D4AF37]">
            <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-[#C5A059]/30 rounded-full flex-shrink-0">
              <span className="text-sm md:text-lg">🛡️</span>
            </div>
            <div className="min-w-0">
              <p className="text-[#D4AF37] font-sans text-[10px] md:text-xs uppercase tracking-wider md:tracking-widest font-semibold">
                JBSA Base Access
              </p>
              <p className="text-white/70 font-sans text-[10px] md:text-xs tracking-wide group-hover:text-white transition-colors truncate">
                Lackland • Ft. Sam • Randolph
              </p>
            </div>
          </div>
        </div>
      </Link>

      {/* Background Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: isMobile ? 1.4 : 1.1, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0"
        >
          {/* Ken Burns Zoom + Pan Container - Alternating directions */}
          <motion.div
            key={`ken-burns-${currentSlide}`}
            initial={{
              scale: 1,
              x: currentSlide === 0 ? (isMobile ? "-1%" : "-2%") : currentSlide === 1 ? (isMobile ? "1%" : "2%") : (isMobile ? "-0.5%" : "-1%"),
            }}
            animate={{
              scale: isMobile ? 1.04 : 1.08,
              x: currentSlide === 0 ? (isMobile ? "1%" : "2%") : currentSlide === 1 ? (isMobile ? "-1%" : "-2%") : (isMobile ? "0.5%" : "1%"),
            }}
            transition={{
              duration: 5.4,
              ease: [0.22, 0.61, 0.36, 1],
            }}
            className="absolute inset-0"
          >
            <SlideMedia
              slide={currentSlide === 2 ? { ...activeSlide, video: audiQ7HeroVideo.url, image: heroSlideStudio } : activeSlide}
              index={currentSlide}
              isActive
              isMobile={isMobile}
              videoRef={activeVideoRef}
            />
          </motion.div>

          {/* Dark Gradient Overlay - Cinematic */}
          <div
            className="absolute inset-0 z-[2]"
            style={{
              background: currentSlide === 0
                ? isMobile
                  ? 'linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.36) 32%, rgba(0,0,0,0.5) 100%)'
                  : 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%)'
                : isMobile
                  ? 'linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.48) 34%, rgba(0,0,0,0.68) 100%)'
                  : 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 flex h-full items-start md:items-center justify-center px-5 md:px-6 pt-28 md:pt-0 pb-44 md:pb-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center max-w-[20rem] sm:max-w-2xl md:max-w-4xl mx-auto mt-[22vh] md:mt-0"
          >
            {/* Headline */}
            <h2 
              className="font-playfair text-white mb-3 md:mb-4"
              style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
            >
              <span className="block text-[2.1rem] sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-wide leading-[0.98]">
                {slides[currentSlide].headline}
              </span>
              {slides[currentSlide].headlineAccent && (
                <span className="block text-[2.05rem] sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-[#C5A059] mt-2 leading-[0.98]">
                  {slides[currentSlide].headlineAccent}
                </span>
              )}
            </h2>

            {/* Subtext - Montserrat style */}
            <p 
              className="font-sans text-sm sm:text-lg md:text-xl text-[#f0f0f0] font-light tracking-[0.5px] md:tracking-[1px] leading-relaxed mt-5 md:mt-6 max-w-[18rem] sm:max-w-2xl mx-auto"
            >
              {slides[currentSlide].subtext}
            </p>

          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={handlePrev}
        className="absolute left-4 md:left-8 top-[50%] md:top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 text-white/70 hover:text-white transition-colors duration-300 group rounded-full bg-black/20 backdrop-blur-sm"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-7 h-7 md:w-10 md:h-10 group-hover:scale-110 transition-transform" />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-4 md:right-8 top-[50%] md:top-1/2 -translate-y-1/2 z-20 p-2 md:p-3 text-white/70 hover:text-white transition-colors duration-300 group rounded-full bg-black/20 backdrop-blur-sm"
        aria-label="Next slide"
      >
        <ChevronRight className="w-7 h-7 md:w-10 md:h-10 group-hover:scale-110 transition-transform" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-40 md:bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-2 md:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-10 md:w-16 h-1 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? "bg-[#C5A059]"
                : "bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-20 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            className="w-1.5 h-3 bg-[#C5A059] rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default LuxuryHeroSlider;
